import { useMemo } from 'react'
import { useI18n } from '../../../i18n'
import { formatRelativeTime, severityRank } from '../../../utils/operationalUx'
import type { UnifiedRuntimeEvent } from '../../../types/unifiedExecutiveContext'
import styles from '../../../styles/enterprise/EnterpriseOperationalUx.module.scss'

type Props = {
  events: UnifiedRuntimeEvent[]
  mode?: 'compact' | 'full'
  maxItems?: number
  showPropagation?: boolean
}

function timelineItemClass(severity: string) {
  const r = severityRank(severity)
  if (r >= 4) return `${styles.timelineItem} ${styles.timelineItemCritical}`
  if (r >= 3) return `${styles.timelineItem} ${styles.timelineItemHigh}`
  return styles.timelineItem
}

export default function OperationalRuntimeTimeline({
  events,
  mode = 'compact',
  maxItems = 8,
  showPropagation = true,
}: Props) {
  const { t } = useI18n()

  const sorted = useMemo(
    () =>
      [...(events || [])]
        .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
        .slice(0, maxItems),
    [events, maxItems],
  )

  if (!sorted.length) {
    return (
      <p className={styles.emptyState}>
        {t('enterpriseOperationalUx.context.noRuntimeActivity', 'No operational runtime activity')}
      </p>
    )
  }

  return (
    <div className={mode === 'full' ? styles.timelineFull : styles.timeline}>
      <p className={styles.postureMetricLabel}>
        {t('enterpriseOperationalUx.context.runtimeEventChain', 'Runtime event chain')}
      </p>
      <ul className={styles.timelineList}>
        {sorted.map((ev) => (
          <li key={ev.id} className={timelineItemClass(ev.severity)}>
            <span className={styles.timelineTime}>{formatRelativeTime(ev.at)}</span>
            <span className={styles.timelineEvent}>
              <strong>{ev.eventType}</strong>
              {ev.summary ? ` — ${ev.summary}` : null}
            </span>
            {showPropagation && ev.relatedStandardCode ? (
              <span className={styles.timelineMeta}>
                {t('enterpriseOperationalUx.context.runtimePropagation', 'Runtime propagation')}:{' '}
                {ev.relatedStandardCode}
              </span>
            ) : null}
            {ev.explanation && mode === 'full' ? (
              <span className={styles.timelineExplain}>{ev.explanation}</span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  )
}
